import { makeSvgDataSrc } from "@elements/Advertisement/helper/makeSvgData";
import { ScrollButton } from "@elements/Buttons/ScrollButton";
import Image from "@elements/ResponsiveImage/Image";
import Date from "@elements/Typography/Date";
import {
  ENABLE_ARTICLE_REDESIGN,
  IS_PLAYWRIGHT_TEST,
} from "@helper/getEnvVariables";
import getFallbackSectionImage from "@helper/getFallbackSectionImage";
import { ProcessedArticleData } from "@hooks/transformer/useOSResponse";
import { useBHCustomEventAnalyticsCall } from "@hooks/useAnalyticsCall/useBHCustomEventAnalyticsCall";
import ArticleElementWrapper from "@routes/Article/Components/ArticleElementWrapper";
import Heading from "@sphtech/sph-web-ui/elements/Heading";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { twMerge } from "tailwind-merge";

type TrendingArticleCarouselProps = Readonly<{
  testId?: string;
  rootClassName?: string;
  dataSectionName?: string;
  trendingArticles: ProcessedArticleData[];
  source?: string;
}>;

export enum CAROUSEL_CONTROL_DIRECTION {
  RIGHT = "right",
  LEFT = "left",
}

export default function TrendingArticleCarousel({
  testId,
  rootClassName,
  dataSectionName,
  trendingArticles,
  source,
}: TrendingArticleCarouselProps): React.ReactElement {
  const dummyImage = getFallbackSectionImage(dataSectionName);
  const BHCustomEvent = useBHCustomEventAnalyticsCall();
  const hasRun = useRef(false);
  useEffect(() => {
    if (!hasRun.current) {
      BHCustomEvent(
        "trending-story-middle",
        "visible",
        window.location.origin + window.location.pathname,
        undefined,
        "",
      );
      hasRun.current = true;
    }
  }, [BHCustomEvent]);

  const sliderRef = useRef<HTMLDivElement>(null);
  const [currentDirection, setCurrentDirection] = useState({
    direction: CAROUSEL_CONTROL_DIRECTION.RIGHT,
    scrollLeft: 0,
    activeLeftControl: false,
    activeRightControl: true,
  });

  const handleScroll = () => {
    if (!sliderRef.current) return;
    const scrollMinState = 1; // to avoid floating-point error
    const scrollLeft = sliderRef.current.scrollLeft;
    const scrollWidth = sliderRef.current.scrollWidth;
    const clientWidth = sliderRef.current.clientWidth;
    const scrollRight = scrollWidth - clientWidth - scrollLeft;
    if (scrollLeft > scrollMinState || scrollRight > scrollMinState) {
      setCurrentDirection({
        ...currentDirection,
        activeLeftControl: scrollLeft > scrollMinState,
        activeRightControl: scrollRight > scrollMinState,
      });
    }
  };

  const handleSliderControl = (direction?: CAROUSEL_CONTROL_DIRECTION) => {
    if (!sliderRef.current) return;

    const SCROLL_OFFSET = 270;
    sliderRef.current.scrollBy({
      left:
        direction === CAROUSEL_CONTROL_DIRECTION.RIGHT
          ? SCROLL_OFFSET
          : -SCROLL_OFFSET,
      behavior: "smooth",
    });
  };

  const isArticleRedesignEnabled = ENABLE_ARTICLE_REDESIGN;

  return (
    <ArticleElementWrapper>
      <div className="mt-4 lg:mt-6 w-full h-0.5 bg-brand-primary"></div>
      <div
        className={twMerge(
          "w-full px-0 py-5 mb-5",
          isArticleRedesignEnabled && "mb-6 lg:mb-8",
          rootClassName,
        )}
        data-testid={testId}
        data-section-name={dataSectionName}
      >
        <div className="relative flex">
          <Heading
            Element="h2"
            className="font-primary font-bold text-base leading-sm mb-1 text-blue-400"
          >
            <Link
              reloadDocument
              to={`/quick-news?ref=${source || "trending-story-middle"}`}
              target="_blank"
            >
              Pilihan untuk anda
            </Link>
          </Heading>

          <div className="hidden lg:flex gap-2.5 absolute right-0 top-1">
            <ScrollButton
              isPrevious
              isDisabled={!currentDirection.activeLeftControl}
              onClick={() => {
                handleSliderControl(CAROUSEL_CONTROL_DIRECTION.LEFT);
              }}
            />
            <ScrollButton
              isDisabled={!currentDirection.activeRightControl}
              onClick={() => {
                handleSliderControl(CAROUSEL_CONTROL_DIRECTION.RIGHT);
              }}
            />
          </div>
        </div>
        <div className="text-xs leading-[150%] mb-5">
          Lihat berita utama daripada semua kategori di satu tempat.
        </div>
        <div
          ref={sliderRef}
          onScroll={handleScroll}
          className={twMerge(
            "flex overflow-y-hidden gap-2.5 lg:gap-5 items-start justify-start",
            "hide-scrollbar overflow-x-scroll [-webkit-overflow-scrolling:touch] lg:overflow-x-hidden",
            isArticleRedesignEnabled && "gap-4 lg:gap-4",
          )}
        >
          {IS_PLAYWRIGHT_TEST === "true" ? (
            <div
              data-placeholder="trending-article-carousel"
              className="w-full"
            >
              <div>
                <img
                  style={{ maxWidth: "100%" }}
                  src={makeSvgDataSrc({
                    width: 860,
                    height: 362,
                    text: `Trending Article Carousel`.toUpperCase(),
                  })}
                  alt={`Trending Article Carousel`}
                />
              </div>
            </div>
          ) : (
            <>
              {trendingArticles.map((item) => {
                return (
                  <div className="relative min-w-[250px]" key={item.id}>
                    <Link
                      reloadDocument
                      to={`${item.linkToQuick}?ref=${source || "trending-story-middle"}`}
                      target="_blank"
                    >
                      <div
                        className={twMerge(
                          "relative after:pointer-events-none after:absolute after:inset-x-0",
                          "after:bottom-0 after:h-[196px] after:bg-gradient-to-t after:from-[#000] after:to-transparent",
                          isArticleRedesignEnabled && "after:rounded-[20px]",
                        )}
                      >
                        <Image
                          displayWidth={250}
                          srcWidth={480}
                          srcHeight={320}
                          src={item.imageField?.url ?? dummyImage}
                          alt={item.imageAlt}
                          className={twMerge(
                            "w-[250px] object-cover h-[362px]",
                            isArticleRedesignEnabled && "rounded-[20px]",
                          )}
                        />
                      </div>
                      <div className="absolute bottom-2 w-[245px] p-2.5">
                        <Heading
                          Element="h3"
                          className={twMerge(
                            "text-white-100 text-base font-semibold leading-[150%] font-primary",
                            "overflow-hidden overflow-ellipsis",
                          )}
                        >
                          {item.title}
                        </Heading>

                        <Date
                          date={item.date}
                          time={item.time}
                          variant="or"
                          className="text-white-100"
                        />
                      </div>
                    </Link>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    </ArticleElementWrapper>
  );
}
